import { Armchair, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import { ReactElement, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { useUser } from 'app/utils'
import { formatDayRuns, type TypeSetupProvider } from 'app/lib/aiAgentAvailability'
import type { AppointmentTypeSetup } from './useAppointmentTypeSetups'

const PROVIDERS_SHOWN = 3

/** One provider who can take the type: their name, then when, then which operatories. */
function ProviderRow({ provider }: { provider: TypeSetupProvider }): ReactElement {
  return (
    <li className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-label-sm text-strong">
        {provider.name}
        {provider.backupOnly && (
          <span className="rounded border border-default px-1 text-body-xs font-normal text-subtle">Backup</span>
        )}
      </span>
      <span className="flex items-start gap-1.5 text-body-xs text-default">
        <Clock aria-label="When" className="mt-0.5 h-3 w-3 shrink-0 text-minimal" />
        <span>
          {formatDayRuns(provider.days)}
          {provider.hours && <span className="tabular-nums text-subtle">{` · ${provider.hours}`}</span>}
        </span>
      </span>
      <span className="flex items-start gap-1.5 text-body-xs text-subtle">
        <Armchair aria-label="Operatories" className="mt-0.5 h-3 w-3 shrink-0 text-minimal" />
        <span>{provider.rooms.join(`, `)}</span>
      </span>
    </li>
  )
}

/** Under the strip: the appointment type the agent books, and who the schedule lets take it, where. */
export function TypeSetupDetails({
  setup,
  loading,
  showBooksAs = true,
}: {
  setup: AppointmentTypeSetup | null | undefined
  loading: boolean
  /** Off where the card already names the type and its length. */
  showBooksAs?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const isSuper = useUser()?.type === `super`
  if (loading && setup === undefined) return <div className="mt-4 h-16 animate-pulse rounded bg-surface-muted" />
  if (!setup) {
    return (
      <p className="mt-4 border-t border-default pt-3 text-body-xs text-subtle">
        No appointment type set for the agent.
      </p>
    )
  }
  const shown = expanded ? setup.providers : setup.providers.slice(0, PROVIDERS_SHOWN)
  const hidden = setup.providers.length - shown.length
  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-default pt-3">
      {showBooksAs && (
        <div className="flex items-baseline justify-between gap-3 text-body-xs text-subtle">
          <span>
            Books as <span className="text-default">{setup.typeName}</span>
          </span>
          {setup.minutes && <span className="shrink-0 tabular-nums">{setup.minutes} min</span>}
        </div>
      )}
      {setup.carestackProductionTypeId && (
        <p className="text-body-xs text-subtle">
          CareStack production type
          {isSuper && <span className="tabular-nums text-minimal">{` · ${setup.carestackProductionTypeId}`}</span>}
        </p>
      )}
      {setup.providers.length === 0 ? (
        <p className="text-body-xs text-subtle">
          {setup.carestackProductionTypeId
            ? `Openings come straight from CareStack, whose schedule decides which providers and chairs take it.`
            : `No provider is scheduled for it.`}
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {shown.map((provider) => (
              <ProviderRow key={provider.name} provider={provider} />
            ))}
          </ul>
          {setup.providers.length > PROVIDERS_SHOWN && (
            <Button variant="tertiary" size="sm" className="w-full" onClick={() => setExpanded(!expanded)}>
              {expanded ? `Show less` : `Show ${hidden} more`}
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
